/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import DynamicField from '@linc-technologies/linced-common/components/dynamic-field';
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { cancel, debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { serializeQueryParams } from 'linced-app/utils/url';
import config from 'linced-app/config/environment';
import { classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';

@classNames('id-search-field')
export default class EmailSearchField extends DynamicField {
  // Services
  @service
  fetch;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  store;

  @tracked
  alreadyExists = false;
  @tracked
  conflictDialogOpen = false;
  @tracked
  mergeDialogOpen = false;
  readOnly = false;

  @or('readOnly', 'field.isReadOnly')
  isReadOnly;

  @tracked
  searchTimer = null;
  @tracked
  isSearching = false;

  @observes('entityField.value')
  _watchValue() {
    // If it matches the caregiver's current email address, don't do anything.
    if (!this.get('entityField.hasDirtyAttributes')) {
      this.resetSearchResult();
      return;
    }

    let fieldValue = this.entityField.get('value');
    if (
      !isEmpty(fieldValue) &&
      !isEmpty(this.element.querySelector('input:valid'))
    ) {
      // Non-empty and valid; initiate search.
      this.searchTimer = debounce(this, 'search', 200, false);
    } else {
      // Empty or otherwise invalid; cancel any pending search.
      if (!isEmpty(this.searchTimer)) {
        cancel(this.searchTimer);
        this.searchTimer = null;
      }
      this.resetSearchResult();
    }
  }

  @tracked conflictedEmailAddress = '';

  @tracked
  existingPerson;

  @tracked
  searchResult;

  @computed('conflictedEmailAddress', 'customValidations.[]')
  get emailValidations() {
    if (isEmpty(this.conflictedEmailAddress)) {
      // No conflict to check against - return upstream custom validations.
      // This allows the person-form to tell us if there was a conflict
      // (with a non-caregiver).
      return isArray(this.customValidations) ? this.customValidations : [];
    }
    return [
      {
        message: 'This email address is already in use',
        validate: (val) => {
          return (
            val.toLowerCase().trim() !==
            this.conflictedEmailAddress.toLowerCase().trim()
          );
        },
      },
    ];
  }

  @action
  closeConflictDialog() {
    this.conflictDialogOpen = false;
  }

  @action
  checkIfExists() {
    if (!isEmpty(this.entityField.get('value')) && this.alreadyExists) {
      this.conflictDialogOpen = true;
    }
  }

  @action
  openMergeDialog() {
    // Close the conflict dialog when opening the merge dialog.
    // This prevents the larger conflict dialog from protruding beyond the
    // smaller merge dialog's edge, which could cause confusion.
    this.mergeDialogOpen = true;
    this.conflictDialogOpen = false;
  }

  @action
  closeMergeDialog() {
    this.mergeDialogOpen = false;
  }

  @action
  mergeComplete(succeeded, mergedPersonId) {
    this.mergeDialogOpen = false;
    this.conflictDialogOpen = false;
    this.conflictedEmailAddress = '';
    this.alreadyExists = false;
    if (succeeded) {
      // Roll back unsaved attributes (i.e. email address) to prevent the
      // unsaved changes confirmation prompt when navigating to the merged person.
      this.entity.rollbackAttributes();
      if (mergedPersonId !== this.get('entity.id')) {
        let selectedTab =
          this.router.get('currentRoute.queryParams.selectedTab') ?? 0;
        this.router.transitionTo('people.person.information', mergedPersonId, {
          queryParams: { selectedTab },
        });
      } else {
        this.entity.reload();
      }
    }
  }

  search() {
    let fieldValue = this.entityField.get('value');
    this.isSearching = true;
    let params = {
      query: fieldValue,
    };
    this.fetch
      .request(
        `${
          config.APP.apiHost
        }/people/v4/caregivers/:search?${serializeQueryParams(params)}`,
      )
      .then((response) => {
        if (!!response && !!response.caregivers && response.caregivers.length) {
          let caregivers = response.caregivers.filter((caregiver) => {
            return caregiver.personId !== this.get('entity.id');
          });
          let caregiver = caregivers[0];

          if (!isEmpty(caregiver)) {
            this.alreadyExists = true;
            this.searchResult = caregiver;
            this.conflictDialogOpen = true;
            this.isSearching = false;
            this.conflictedEmailAddress = fieldValue;

            if (caregiver.inThisSchool) {
              // Fetch and populate the existing person, so that we can render a full user-badge-card for them.
              this.store
                .findRecord('person', caregiver.personId)
                .then((person) => {
                  this.existingPerson = person;
                });
            }
          } else {
            this.resetSearchResult();
          }
        } else {
          this.resetSearchResult();
        }
      });
  }

  resetSearchResult() {
    this.alreadyExists = false;
    this.searchResult = null;
    this.existingPerson = null;
    this.conflictDialogOpen = false;
    this.isSearching = false;
  }
}
